// index.js
import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import TopBarHeader from "../../components/TopBarHeader";
import Uploader from "../../components/Uploader";
import Table from "./Table";
import Modal from "../../components/Modal";
import VisorPDF from "../../components/VisorPDF";
import * as XLSX from "xlsx";
import { PDFDocument } from "pdf-lib";
import { useAuth } from "../../components/AuthContext";



const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const validarTamanoArchivo = (archivo) => archivo.size <= MAX_FILE_SIZE;

const MAX_PAGES = 100;

const validarNumeroPaginas = async (archivo) => {
  try {
    const arrayBuffer = await archivo.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const numPages = pdfDoc.getPageCount();
    return numPages <= MAX_PAGES;
  } catch (error) {
    console.error("Error al leer el archivo PDF:", error);
    return false;
  }
};

const descargarExcel = (datos, fileName) => {
  const worksheet = XLSX.utils.json_to_sheet(
    datos
      .map((resultado) => {
        return resultado.Asistencia.map((asistencia) => ({
          Nombre: resultado.NOMBRE,
          RUT: resultado.RUT, // Agregar RUT
          Código: resultado.CODIGO, // Agregar Código
          Fecha: `${asistencia.DIA}-${resultado.MES}-${resultado.ANO}`,
          "Hora Entrada": asistencia.HORA_ENTRADA,
          "Hora Salida": asistencia.HORA_SALIDA,
          Observaciones: asistencia.HORA_ENTRADA === "AUSENTE"
            ? "Ausente"
            : resultado.OBSERVACIONES || "",
        }));
      })
      .flat()
  );
  

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Asistencia");

  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

const generarIdentificadorUnico = () => {
  const fecha = new Date().toISOString();
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${fecha}-${randomStr}`;
};

function LibroAsistencia() {
  const [errorMensaje, setErrorMensaje] = useState("");
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCedula, setSelectedCedula] = useState(null);
  const { currentUser } = useAuth(); 
const userId = currentUser ? currentUser.email : null; 


  useEffect(() => {
    // Puedes eliminar este useEffect si no realiza ninguna acción
  }, [resultados, cargando]);

  const openModal = (resultado, tipo) => {
    if (tipo === "pdf") {
      const blob = new Blob([resultado.archivoOriginal], {
        type: resultado.archivoOriginal.type,
      });
      const archivoURL = URL.createObjectURL(blob);
      setSelectedCedula({ archivoURL });
    }
    setIsModalOpen(true);
  };

  const formatearMes = (mes) => {
    const meses = {
      enero: "01",
      febrero: "02",
      marzo: "03",
      abril: "04",
      mayo: "05",
      junio: "06",
      julio: "07",
      agosto: "08",
      septiembre: "09",
      octubre: "10",
      noviembre: "11",
      diciembre: "12",
    };

    if (!mes) return "N/A";
    if (meses[mes.toLowerCase()]) return meses[mes.toLowerCase()];
    if (/^\d{2}$/.test(mes)) return mes;
    return "N/A";
  };

  const closeModal = () => setIsModalOpen(false);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const archivosValidos = [];

      for (const archivo of acceptedFiles) {
        const isValid = await esArchivoValido(archivo);
        if (isValid) {
          archivosValidos.push(archivo);
        }
      }

      if (archivosValidos.length > 0) {
        const archivosIniciales = archivosValidos.map((archivo) => ({
          archivoName: archivo.name,
          estado: "procesando",
          archivoOriginal: archivo,
          NOMBRE: "",
          MES: "",
          ANO: "",
          Asistencia: [],
          archivoId: generarIdentificadorUnico(), // Asegurar que archivoId esté presente
          OBSERVACIONES: "Sin observaciones", // Inicializar OBSERVACIONES
        }));

        setResultados((prevResultados) => [
          ...prevResultados,
          ...archivosIniciales,
        ]);

        archivosValidos.forEach((archivo) => {
          enviarArchivo(archivo, archivo.name);
        });
      }
    },
    [resultados]
  );

  const enviarArchivo = async (
    archivo,
    archivoName,
    reintento = false,
    intentos = 0
  ) => {
    const MAX_INTENTOS = 3;

    try {
      const archivoId =
        resultados.find((r) => r.archivoName === archivoName)?.archivoId ||
        generarIdentificadorUnico();
      const base64 = await fileToBase64(archivo);

      const payload = {
        userId: userId, // Agrega el userId al payload
        rawDocument: {
          mimeType: archivo.type,
          content: base64,
        },
      };
    

      const apiUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_STAGE}/gemini/asistencia/libro`;
      let response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      let data = await response.json();
      console.log(data);
      if (!data || !data.body || !data.body.Asistencia) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? {
                  ...resultado,
                  estado: "error",
                  mensajeError: "Respuesta inesperada de la API",
                }
              : resultado
          )
        );
        throw new Error("Respuesta inesperada de la API");
      }

      const nombre = data.body.NOMBRE || "N/A";
      const mes = formatearMes(data.body.MES);
      const ano = data.body.ANO || "N/A";
      const observaciones = data.body.OBSERVACIONES || "Sin observaciones";
      const rut = data.body.RUT || "N/A";
const codigo = data.body.CODIGO || "N/A";


      const asistencia = data.body.Asistencia.map((asistencia) => ({
        DIA: asistencia.DIA || "N/A",
        HORA_ENTRADA: asistencia.HORA_ENTRADA || "N/A",
        HORA_SALIDA: asistencia.HORA_SALIDA || "N/A",
      }));

      const resultadoConDatos = {
        archivoId,
        archivoName,
        estado: "Listo",
        NOMBRE: nombre,
        RUT: rut, // Agregar RUT
        CODIGO: codigo, // Agregar codigo
        MES: mes,
        ANO: ano,
        Asistencia: asistencia,
        OBSERVACIONES: observaciones,
        archivoOriginal: archivo, // Guardar el archivo original para el visor
      };
      

      setResultados((prevResultados) =>
        prevResultados.map((resultado) =>
          resultado.archivoName === archivoName
            ? { ...resultado, ...resultadoConDatos }
            : resultado
        )
      );

      return { success: true, resultado: resultadoConDatos };
    } catch (error) {
      console.error("Error al procesar el archivo:", error);

      if (intentos < MAX_INTENTOS) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? { ...resultado, estado: "reintentando", mensajeError: error.message }
              : resultado
          )
        );

        await new Promise((resolve) => setTimeout(resolve, 3000));
        return enviarArchivo(archivo, archivoName, true, intentos + 1);
      } else {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? { ...resultado, estado: "error", mensajeError: error.message }
              : resultado
          )
        );
        return { success: false, error: error.toString(), resultado: null };
      }
    }
  };

  const reintentarEnvio = (archivoName) => {
    setResultados((prevResultados) =>
      prevResultados.map((resultado) =>
        resultado.archivoName === archivoName
          ? {
              ...resultado,
              estado: "procesando",
              mensajeError: "",
              Asistencia: [],
              OBSERVACIONES: "Sin observaciones", // Resetear observaciones si es necesario
            }
          : resultado
      )
    );

    const archivo = resultados.find(
      (resultado) => resultado.archivoName === archivoName
    );
    if (archivo && archivo.archivoOriginal) {
      enviarArchivo(archivo.archivoOriginal, archivoName, true);
    }
  };

  const esArchivoValido = async (archivo) => {
    if (!validarTamanoArchivo(archivo)) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el tamaño máximo permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    if (archivo.type !== "application/pdf") {
      setErrorMensaje(
        `Solo se permiten archivos PDF. El archivo ${archivo.name} no es válido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    const esValido = await validarNumeroPaginas(archivo);
    if (!esValido) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el número máximo de páginas permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    return true;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
  });

  /**
   * handleUpdateField actualiza los campos de Asistencia y también los campos adicionales como NOMBRE y OBSERVACIONES
   * @param {string} archivoId - Identificador único del archivo
   * @param {number} asistenciaIndex - Índice de la asistencia en el arreglo Asistencia
   * @param {object} updatedAsistencia - Objeto con los campos de asistencia actualizados
   * @param {object} updatedFields - Objeto con los campos adicionales actualizados (NOMBRE, OBSERVACIONES, DIA, MES, ANO)
   */
  const handleUpdateField = (
    archivoId,
    asistenciaIndex,
    updatedAsistencia,
    updatedFields = {}
  ) => {
    setResultados((prevResultados) =>
      prevResultados.map((resultado) =>
        resultado.archivoId === archivoId
          ? {
              ...resultado,
              Asistencia: resultado.Asistencia.map((asist, idx) =>
                idx === asistenciaIndex ? { ...asist, ...updatedAsistencia } : asist
              ),
              // Actualizar campos adicionales si se proporcionan
              ...updatedFields,
            }
          : resultado
      )
    );
  };

  return (
    <div {...getRootProps()} className="digitalizador-cedulas">
      <input {...getInputProps()} style={{ display: "none" }} />
      <TopBarHeader />
      <h1 className="h1-titulo-2">Libros de Asistencia</h1>
      <p className="description">
        Identifica y procesa datos de libros de asistencia utilizando AI
        Generativa.
      </p>

      <Uploader onResultadosActualizados={setResultados} inputProps={getInputProps} />
      {errorMensaje && <div className="error-mensaje">{errorMensaje}</div>}
      {resultados.length === 0 && cargando && (
        <div className="loader-container">
          <p>Procesando archivos...</p>
          <div className="loader"></div>
        </div>
      )}

      {resultados.length > 0 && (
        <>
          <button
            className="button"
            onClick={() => descargarExcel(resultados, "ResultadosAsistencia")}
            disabled={resultados.length === 0}
          >
            Descargar Excel
          </button>
          <div className="contenedor-tabla">
            {resultados.map((resultado) => (
              <Table
                key={resultado.archivoId} // Usar archivoId como key
                resultados={resultado}
                onOpenModal={openModal}
                reintentarEnvio={reintentarEnvio}
                onUpdateField={handleUpdateField}
              />
            ))}
          </div>
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedCedula && selectedCedula.archivoURL && (
          <VisorPDF archivo={selectedCedula.archivoURL} />
        )}
        {selectedCedula && selectedCedula.imagen && (
          <img
            src={`data:image/png;base64,${selectedCedula.imagen}`}
            alt="Imagen AFP"
          />
        )}
      </Modal>
      {cargando && (
        <div className="loader-container">
          <div className="loader-content">
            <div className="loader"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LibroAsistencia;
